import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { getVeterinerlerForZiyaret, addVisit, updateVisit } from '../services/api';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Stack, CircularProgress, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './FormStyles.css';

const parseDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date && !isNaN(dateInput)) {
        return dateInput;
    }
    let date;
    if (typeof dateInput === 'object' && dateInput !== null && dateInput.year && dateInput.month && dateInput.day) {
        date = new Date(Date.UTC(dateInput.year, dateInput.month - 1, dateInput.day));
    } else {
        date = new Date(dateInput);
    }
    return !isNaN(date.getTime()) ? date : null;
};

const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ZiyaretForm = ({ open, onClose, onSave, kumesId, donemId, ziyaretData }) => {
    
    const initialState = {
        VETERINERLIK_ID: '',
        TARIH: formatDateForInput(new Date()),
        SAAT: new Date().toTimeString().slice(0, 5),
        VETERINER_NOTU: '',
        ZIYARET_GUN_AGIRLIK_GR: '',
        TOPLAM: '', 
    };

    const [formData, setFormData] = useState(initialState);
    const [veterinerler, setVeterinerler] = useState([]);
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const isEditMode = Boolean(ziyaretData);

    useEffect(() => {
        if (!open) return;
        const loadVeterinarians = async () => {
            if (!kumesId) return;
            setLoading(true);
            try {
                const res = await getVeterinerlerForZiyaret(kumesId);
                setVeterinerler(res.data.data || []);
            } catch (err) {
                enqueueSnackbar('Veteriner listesi yüklenemedi.', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };
        loadVeterinarians();
        
        if (isEditMode && ziyaretData) {
            const validDate = parseDate(ziyaretData.ZAMAN || ziyaretData.TARIH);
            setFormData({
                VETERINERLIK_ID: ziyaretData.VETERINERLIK_ID || '',
                TARIH: validDate ? formatDateForInput(validDate) : initialState.TARIH,
                SAAT: validDate ? validDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : initialState.SAAT,
                VETERINER_NOTU: ziyaretData.VETERINER_NOTU || '',
                ZIYARET_GUN_AGIRLIK_GR: ziyaretData.ZIYARET_GUN_AGIRLIK_GR || '',
                
                TOPLAM: ziyaretData.GENEL_TOPLAM || '', 
            });
        } else {
            setFormData(initialState);
        }
    }, [open, kumesId, ziyaretData, isEditMode]);

    

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            DONEM_ID: donemId,
            VETERINERLIK_ID: formData.VETERINERLIK_ID,
            TARIH: formData.TARIH.split('-').reverse().join('.'),
            SAAT: formData.SAAT,
            VETERINER_NOTU: formData.VETERINER_NOTU,
            ZIYARET_GUN_AGIRLIK_GR: Number(formData.ZIYARET_GUN_AGIRLIK_GR) || 0,
            
            KDV_ORAN: 0,
            
            
            
            TOPLAM: Number(formData.TOPLAM) || 0,
        };
        setLoading(true);
        try {
            if (isEditMode) {
                payload.DONEM_VETERINERLIK_ID = ziyaretData.DONEM_VETERINERLIK_ID;
                await updateVisit(payload);
                enqueueSnackbar('Ziyaret başarıyla güncellendi.', { variant: 'success' });
            } else {
                await addVisit(payload);
                enqueueSnackbar('Ziyaret başarıyla eklendi.', { variant: 'success' });
            }
            onSave();
        } catch (err) {
            enqueueSnackbar(`İşlem başarısız: ${err.message}`, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {isEditMode ? 'Ziyaret Düzenle' : 'Yeni Ziyaret Ekle'}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: '#FF7A2B' }} /></Box> : (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <div className="form-section">
                                <h4 className="form-section-header">Genel Bilgiler</h4>
                                <Stack spacing={2.5}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Veteriner</InputLabel>
                                        <Select name="VETERINERLIK_ID" value={formData.VETERINERLIK_ID} label="Veteriner" onChange={handleChange}>
                                            <MenuItem value="" disabled>Seçim yapınız...</MenuItem>
                                            {veterinerler.map(vet => <MenuItem key={vet.VETERINERLIK_ID} value={vet.VETERINERLIK_ID}>{vet.VETERINERLIK_ADI}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField label="Tarih" type="date" name="TARIH" value={formData.TARIH} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
                                    <TextField label="Saat" type="time" name="SAAT" value={formData.SAAT} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
                                </Stack>
                            </div>
                            <div className="form-section">
                                <h4 className="form-section-header">Ziyaret Detayları</h4>
                                <Stack spacing={2.5}>
                                    <TextField label="Veteriner Notu" name="VETERINER_NOTU" value={formData.VETERINER_NOTU} onChange={handleChange} multiline rows={3} fullWidth />
                                    <TextField label="Canlı Ağırlık (gr)" type="number" name="ZIYARET_GUN_AGIRLIK_GR" value={formData.ZIYARET_GUN_AGIRLIK_GR} onChange={handleChange} fullWidth />
                                </Stack>
                            </div>
                            <div className="form-section">
                                <h4 className="form-section-header">Finansal Bilgiler</h4>
                                <Stack spacing={2.5}>
                                    {/* KDV alanları kaldırıldı, sadece Toplam Tutar kaldı */}
                                    <TextField label="Toplam Tutar (₺)" type="number" name="TOPLAM" value={formData.TOPLAM} onChange={handleChange} inputProps={{ step: "any" }} required fullWidth />
                                </Stack>
                            </div>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={onClose}>İptal</Button>
                    <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#FF7A2B', '&:hover': { backgroundColor: '#E66A20' } }}>
                        {isEditMode ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default ZiyaretForm;